import { useHeadToHead, formatH2HStats } from "@/hooks/useHeadToHead";
import { useAutoFetchH2H } from "@/hooks/useAutoFetchH2H";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TeamRecentGamesDisplay } from "./TeamRecentGamesDisplay";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import { Loader2, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface HeadToHeadStatsProps {
  teamHomeId: string | null;
  teamAwayId: string | null;
  teamHomeApiId: number | null;
  teamAwayApiId: number | null;
  homeTeamName: string;
  awayTeamName: string;
  homeTeamLogo?: string;
  awayTeamLogo?: string;
  currentGameId?: string;
  inScrollableContainer?: boolean;
}

export function HeadToHeadStats({ 
  teamHomeId, 
  teamAwayId,
  teamHomeApiId,
  teamAwayApiId,
  homeTeamName, 
  awayTeamName,
  homeTeamLogo,
  awayTeamLogo,
  currentGameId,
  inScrollableContainer = false
}: HeadToHeadStatsProps) {
  const { data, isLoading } = useHeadToHead(teamHomeId, teamAwayId);
  const navigate = useNavigate();
  
  // Auto-fetch H2H data if not available
  const { isFetching } = useAutoFetchH2H({
    teamHomeApiId,
    teamAwayApiId,
    hasExistingData: (data?.matches?.length ?? 0) > 0,
    enabled: !!teamHomeApiId && !!teamAwayApiId
  });
  
  if (isLoading || isFetching) {
    return (
      <div className="space-y-3">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          Head-to-Head
          {isFetching && <Loader2 className="h-4 w-4 animate-spin" />}
        </h3>
        <div className="animate-pulse space-y-3">
          <div className="h-24 bg-muted rounded" />
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-16 bg-muted rounded" />
          ))}
        </div>
        {isFetching && (
          <p className="text-xs text-muted-foreground text-center mt-4">
            Fetching historical data...
          </p>
        )}
      </div>
    );
  }

  if (!data?.stats || data.matches.length === 0) {
    return (
      <div className="space-y-3">
        <h3 className="text-lg font-semibold">Head-to-Head</h3>
        <p className="text-sm text-muted-foreground text-center py-4">
          No historical data available
        </p>
      </div>
    );
  }

  const formatted = formatH2HStats(data.stats);
  const homeTeamShortName = homeTeamName.substring(0, 3).toUpperCase();
  const awayTeamShortName = awayTeamName.substring(0, 3).toUpperCase();

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold">Head-to-Head</h3>
      </div>

      <Tabs defaultValue="h2h" className="w-full">
        <TabsList className="w-full grid grid-cols-2">
          <TabsTrigger value="h2h">Direct Meetings</TabsTrigger>
          <TabsTrigger value="total">TOTAL</TabsTrigger>
        </TabsList>

        {/* Tab 1: Direct H2H */}
        <TabsContent value="h2h" className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Last {data.matches.length} matches between teams
          </p>

          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4 p-4 rounded-lg bg-muted/50">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">
                {data.stats.home_wins}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {homeTeamName}
              </div>
              {formatted && (
                <div className="text-xs font-medium mt-1">
                  {formatted.homeWinPercentage}%
                </div>
              )}
            </div>
            
            <div className="text-center border-x">
              <div className="text-2xl font-bold text-muted-foreground">
                {data.stats.draws}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Draws
              </div>
              {formatted && (
                <div className="text-xs font-medium mt-1">
                  {formatted.drawPercentage}%
                </div>
              )}
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">
                {data.stats.away_wins}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {awayTeamName}
              </div>
              {formatted && (
                <div className="text-xs font-medium mt-1">
                  {formatted.awayWinPercentage}%
                </div>
              )}
            </div>
          </div>

          {/* Goal Stats */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex justify-between p-2 rounded bg-muted/30">
              <span className="text-muted-foreground">Goals scored</span>
              <span className="font-semibold">{data.stats.home_goals}</span>
            </div>
            <div className="flex justify-between p-2 rounded bg-muted/30">
              <span className="text-muted-foreground">Goals scored</span>
              <span className="font-semibold">{data.stats.away_goals}</span>
            </div>
          </div>

          {formatted?.mostCommonResult && (
            <div className="text-center p-3 rounded-lg border">
              <p className="text-xs text-muted-foreground mb-1">
                Most common result
              </p>
              <p className="text-lg font-bold">
                {formatted.mostCommonResult === 'home' ? homeTeamName : 
                 formatted.mostCommonResult === 'away' ? awayTeamName : 'Draw'}
              </p>
            </div>
          )}

          {/* Recent Matches */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Recent Matches</h4>
            <div className={cn(
              "space-y-2",
              !inScrollableContainer && "max-h-[300px] overflow-y-auto"
            )}>
              <TooltipProvider>
                {data.matches.map((match) => {
                  // Check if teams are in the same position as the current fixture
                  const isHomeTeamAtHome = match.team_home_id === teamHomeId;
                  
                  // Always use original scores - team names/logos are already adjusted to historical positions
                  const displayHomeScore = match.home_score;
                  const displayAwayScore = match.away_score;
                  
                  // Adjust team names and logos based on actual home/away positions in historical match
                  const displayHomeTeamName = isHomeTeamAtHome ? homeTeamName : awayTeamName;
                  const displayAwayTeamName = isHomeTeamAtHome ? awayTeamName : homeTeamName;
                  const displayHomeTeamLogo = isHomeTeamAtHome ? homeTeamLogo : awayTeamLogo;
                  const displayAwayTeamLogo = isHomeTeamAtHome ? awayTeamLogo : homeTeamLogo;
                  
                  // Calculate results based on ADJUSTED scores
                  const isHomeWin = (displayHomeScore ?? 0) > (displayAwayScore ?? 0);
                  const isAwayWin = (displayAwayScore ?? 0) > (displayHomeScore ?? 0);
                  const isDraw = displayHomeScore === displayAwayScore;

                  return (
                    <Tooltip key={match.id}>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => {
                            if (currentGameId) {
                              sessionStorage.setItem('returnContext', JSON.stringify({
                                game: currentGameId,
                                tab: 'h2h'
                              }));
                            }
                            navigate(`/game/${match.fixture_id}`);
                          }}
                          className="w-full p-3 rounded-lg border bg-card hover:bg-accent/70 transition-colors cursor-pointer group"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(match.match_date), "dd MMM yyyy", { locale: enUS })}
                            </span>
                            <div className="flex items-center gap-2">
                              {match.venue && (
                                <span className="text-xs text-muted-foreground">
                                  {match.venue}
                                </span>
                              )}
                              <ExternalLink className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-1">
                              <span className={`text-sm ${isHomeWin ? 'font-bold' : ''}`}>
                                {displayHomeTeamName}
                              </span>
                            </div>
                            
                            <div className="flex items-center gap-2 mx-4">
                              <Badge variant={isDraw ? "secondary" : isHomeWin ? "default" : "outline"}>
                                {displayHomeScore ?? 0}
                              </Badge>
                              <span className="text-muted-foreground">-</span>
                              <Badge variant={isDraw ? "secondary" : isAwayWin ? "default" : "outline"}>
                                {displayAwayScore ?? 0}
                              </Badge>
                            </div>
                            
                            <div className="flex items-center gap-2 flex-1 justify-end">
                              <span className={`text-sm ${isAwayWin ? 'font-bold' : ''}`}>
                                {displayAwayTeamName}
                              </span>
                            </div>
                          </div>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Click to view full match details</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </TooltipProvider>
            </div>
          </div>
        </TabsContent>

        {/* Tab 2: TOTAL (all recent games) */}
        <TabsContent value="total" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:divide-x md:divide-border">
            <div className="md:pr-3">
              {teamHomeId && (
                <TeamRecentGamesDisplay
                  teamId={teamHomeId}
                  teamName={homeTeamName}
                  teamLogo={homeTeamLogo}
                  location="all"
                />
              )}
            </div>
            
            <div className="md:pl-3">
              {teamAwayId && (
                <TeamRecentGamesDisplay
                  teamId={teamAwayId}
                  teamName={awayTeamName}
                  teamLogo={awayTeamLogo}
                  location="all"
                />
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
